/**
 * The printable **Job Ticket** for a screen-print run — the run sheet's sibling, the paper that travels
 * with the screens so a frame and its recipe never part company (plan § 4.3). Summary boxes · the
 * screens table in head order (burned ☐ by pen) · the carousel in ink · art & placement · blanks to
 * pull · per-setup press ticks · sign-off.
 *
 * Same mechanism as the pick list and the pull sheet: a body-level portal (`id="job-ticket-print"`) that
 * `@media print` isolates (globals.css), plus `window.print()`. Ink on white — every state is weight,
 * rule, outline or hatching, never colour, so nothing is lost on a B&W laser.
 */
import { Printer, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import type { BlankPull } from './batch-lib';
import { PressCarousel } from './press-carousel';
import type { SetupWithSpec } from './screen-print-packet-lib';
import { cureLines, printInchesLabel, ringScreensForRun } from './screen-print-packet-lib';
import type { PressShape } from './screen-print-spec-lib';

/** Outlined checkbox — ticked by pen on the printed sheet. */
function Tick() {
  return (
    <span className="inline-block size-[13px] shrink-0 rounded-[2px] border-[1.25px] border-current align-[-2px]" />
  );
}

export function JobTicket({
  label,
  brand,
  garments,
  setups,
  press,
  pulls,
  onClose,
  onPrint,
}: {
  label: string;
  brand: string;
  garments: number;
  setups: readonly SetupWithSpec[];
  press: PressShape & { name: string };
  pulls: readonly BlankPull[];
  onClose: () => void;
  /** Called as Print is tapped — the packet stamps the sheet (this page carries the pull rows; pulled-blanks
   *  v2 D6: every surface that prints the rows stamps). Advisory, never awaited. */
  onPrint?: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const screenCount = setups.every((s) => s.spec)
    ? setups.reduce((n, s) => n + s.screens.length, 0)
    : undefined;
  const cure = cureLines(setups);
  const pullTotal = pulls.reduce((s, p) => s + p.qty, 0);
  const printedOn = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return createPortal(
    <div
      id="job-ticket-print"
      className="fixed inset-0 z-50 overflow-y-auto bg-background/95 p-6 print:static print:overflow-visible print:bg-white print:p-0"
    >
      {/* Toolbar — screen only */}
      <div className="mx-auto mb-4 flex max-w-[816px] items-center justify-between gap-2 print:hidden">
        <p className="text-sm text-muted-foreground">Job ticket · {label}</p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => {
              onPrint?.();
              window.print();
            }}
          >
            <Printer className="size-4" /> Print
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close job ticket">
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-[816px] bg-white p-1 text-[12px] leading-[1.4] text-[#1a1714]">
        {/* masthead */}
        <div className="flex items-center gap-4">
          <img src="/fsp-logo.png" alt="Fast Screen Print" className="h-[52px] w-auto" />
          <div className="min-w-0 flex-1">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-[#625d57]">
              Screen print · Production control
            </div>
            <div className="text-[25px] font-extrabold uppercase leading-none tracking-[0.015em]">
              Job Ticket
            </div>
            <div className="text-[11.5px] text-[#625d57]">
              Burn · mix · set up · press — by screen
            </div>
          </div>
          <div className="whitespace-nowrap text-right font-mono text-[10px] leading-[1.5] text-[#625d57]">
            <span className="block text-[14px] text-[#1a1714]">{label}</span>
            {brand}
            <br />
            Printed {printedOn}
          </div>
        </div>
        <div className="mt-3 border-t-[2.5px] border-[#141210]" />

        {/* summary */}
        <div className="mt-4 grid grid-cols-5 overflow-hidden rounded-[5px] border border-[#141210]">
          {[
            { lbl: 'Garments', val: String(garments) },
            { lbl: 'Setups', val: String(setups.length) },
            { lbl: 'Screens', val: screenCount != null ? String(screenCount) : '—' },
            { lbl: 'Press', val: press.name },
            { lbl: 'Cure', val: cure.find((c) => c.cure)?.cure ?? '—' },
          ].map((c, i) => (
            <div
              key={c.lbl}
              className={i < 4 ? 'border-r border-[#141210] px-3 py-2' : 'px-3 py-2'}
            >
              <div className="text-[8.5px] font-bold uppercase tracking-[0.11em] text-[#625d57]">
                {c.lbl}
              </div>
              <div className="mt-0.5 truncate font-mono text-[15px] font-semibold tabular-nums">
                {c.val}
              </div>
            </div>
          ))}
        </div>

        {/* screens */}
        <div className="mt-4 mb-1.5 flex items-baseline justify-between gap-2">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[0.12em]">
            Screens — burn in this order
          </h2>
          <span className="text-[9.5px] text-[#625d57]">
            tick when burned · mesh and frame on the tape
          </span>
        </div>
        <div className="rounded-[5px] border border-[#141210]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="[&>th]:border-b [&>th]:border-[#141210] [&>th]:px-2 [&>th]:py-1.5 [&>th]:text-[8.5px] [&>th]:font-bold [&>th]:uppercase [&>th]:tracking-[0.09em] [&>th]:text-[#625d57]">
                <th className="w-[38px] text-center">Burned</th>
                <th className="w-[36px]">Head</th>
                <th className="text-left">Screen</th>
                <th className="text-left">Pantone</th>
                <th className="text-left">Ink · mix</th>
                <th className="w-[44px]">Mesh</th>
                <th className="w-[72px]">Frame</th>
                <th className="w-[44px]">Flash</th>
              </tr>
            </thead>
            <tbody>
              {setups.map((s) =>
                s.screens.length > 0 ? (
                  s.screens.map((c) => (
                    <tr
                      key={`${s.key}-${c.seq}`}
                      className="[&>td]:border-b [&>td]:border-[#e2ded7] last:[&>td]:border-0 [&>td]:px-2 [&>td]:py-1.5 [&>td]:align-top"
                    >
                      <td className="text-center">
                        <Tick />
                      </td>
                      <td className="text-center font-mono font-semibold">{c.head ?? c.seq}</td>
                      <td>
                        <span className="font-semibold capitalize">{s.location}</span>
                        {' · '}
                        {s.designName ?? s.designId ?? 'design'} ·{' '}
                        {s.sizeBand === 'youth' ? 'Youth' : 'Adult'}
                        <br />
                        <span className="text-[#625d57]">{c.name}</span>
                      </td>
                      <td>{c.pantone ?? '—'}</td>
                      <td>{c.ink ?? '—'}</td>
                      <td className="text-center font-mono">{c.meshCount ?? '—'}</td>
                      <td>{c.frame ?? '—'}</td>
                      <td className="text-center">
                        {c.flashAfter ? (
                          <span className="rounded-[3px] border-[1.25px] border-current px-1 text-[8.5px] font-extrabold">
                            YES
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr
                    key={s.key}
                    className="[&>td]:border-b [&>td]:border-[#e2ded7] last:[&>td]:border-0 [&>td]:px-2 [&>td]:py-1.5"
                  >
                    <td className="text-center">
                      <Tick />
                    </td>
                    <td className="text-center">—</td>
                    <td colSpan={6}>
                      <span className="font-semibold capitalize">{s.location}</span>
                      {' · '}
                      {s.designName ?? s.designId ?? 'design'} —{' '}
                      <span className="rounded-[3px] border-[1.25px] border-current px-1 text-[8.5px] font-extrabold uppercase">
                        no screen spec
                      </span>{' '}
                      set it in Designs
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>

        {/* carousel + placement */}
        <div className="mt-4 grid grid-cols-[200px_1fr] gap-4 rounded-[5px] border border-[#141210] p-3">
          <PressCarousel
            press={press}
            screens={ringScreensForRun(setups)}
            caption={[press.name, 'head order = print order']}
            mono
            className="w-[200px]"
          />
          <div className="text-[11.5px]">
            <div className="text-[8.5px] font-bold uppercase tracking-[0.11em] text-[#625d57]">
              Art &amp; placement
            </div>
            {setups.map((s) => (
              <div key={s.key} className="mt-1">
                <span className="font-semibold capitalize">{s.location}</span> ·{' '}
                {s.designName ?? s.designId ?? 'design'} ·{' '}
                {s.sizeBand === 'youth' ? 'Youth' : 'Adult'}
                {printInchesLabel(s.design, s.sizeBand)
                  ? ` — ${printInchesLabel(s.design, s.sizeBand)}`
                  : ''}
                {s.design?.alignment?.[s.sizeBand] ? ' · alignment box set' : ''}
              </div>
            ))}
            {cure.length > 0 && (
              <div className="mt-2 text-[#625d57]">
                {cure.map((c) => (
                  <div key={c.design}>
                    {c.design}: {c.cure ?? 'cure not set'}
                    {c.notes ? ` · ${c.notes}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* blanks */}
        <div className="mt-4 mb-1.5 flex items-baseline justify-between gap-2">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[0.12em]">Blanks to pull</h2>
          <span className="text-[9.5px] text-[#625d57]">
            grab all before setting up · {pullTotal} blank{pullTotal === 1 ? '' : 's'}
          </span>
        </div>
        <div className="rounded-[5px] border border-[#141210]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="[&>th]:border-b [&>th]:border-[#141210] [&>th]:px-3 [&>th]:py-1.5 [&>th]:text-[8.5px] [&>th]:font-bold [&>th]:uppercase [&>th]:tracking-[0.09em] [&>th]:text-[#625d57]">
                <th className="w-[34px] text-center">Got</th>
                <th className="text-left">Blank</th>
                <th className="text-left">Colour · size</th>
                <th className="w-[54px] text-right">Qty</th>
              </tr>
            </thead>
            <tbody>
              {pulls.map((p) => (
                <tr
                  key={p.key}
                  className="[&>td]:border-b [&>td]:border-[#e2ded7] last:[&>td]:border-0 [&>td]:px-3 [&>td]:py-1.5"
                >
                  <td className="text-center align-middle">
                    <Tick />
                  </td>
                  <td className="align-middle font-semibold">{p.label}</td>
                  <td className="align-middle text-[#625d57]">{p.variant || p.sizeBand}</td>
                  <td className="text-right align-middle font-mono text-[13px] font-semibold tabular-nums">
                    {p.qty}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* press ticks */}
        <div className="mt-4 mb-1.5 flex items-baseline justify-between gap-2">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[0.12em]">Press</h2>
          <span className="text-[9.5px] text-[#625d57]">
            tick a setup when every garment is through it — the system ticks are on the press
            station
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {setups.map((s) => (
            <div key={s.key} className="rounded-[5px] border border-[#141210] px-3 py-1.5">
              <Tick /> <span className="font-semibold capitalize">{s.location}</span> ·{' '}
              {s.screens.length > 0
                ? s.screens.map((c) => c.name).join(' + ')
                : (s.designName ?? 'design')}{' '}
              · {s.garments} garments
            </div>
          ))}
        </div>

        {/* sign-off */}
        <div className="mt-5 flex items-end justify-between gap-4 border-t border-[#e2ded7] pt-2 font-mono text-[9.5px] text-[#625d57]">
          <span>Fast Screen Print · Job ticket · {label}</span>
          <span className="text-[#1a1714]">
            Burned by <span className="mx-1 inline-block w-[84px] border-b border-[#141210]" /> Set
            up by <span className="mx-1 inline-block w-[84px] border-b border-[#141210]" /> Run by{' '}
            <span className="mx-1 inline-block w-[84px] border-b border-[#141210]" />
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
